import { MONTH_NAMES } from "@/constants";
import type {
  ActionInstructions,
  LimitGranularity,
  PeriodLimit,
  PortfolioInstructionsPatch,
} from "@/types";
import { parseIsoDuration } from "@/utils/durationUtils";
import { sum } from "lodash-es";
import {
  add,
  addDays,
  addMonths,
  differenceInCalendarMonths,
  eachMonthOfInterval,
  eachYearOfInterval,
  endOfDay,
  endOfMonth,
  endOfYear,
  format,
  max as maxDate,
  min as minDate,
  startOfMonth,
} from "date-fns";

export const PERCENTAGE_PRECISION = 2;

export function normalizePercentage(
  value: number | null | undefined
): number | null {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return null;
  }
  return Number.parseFloat(value.toFixed(PERCENTAGE_PRECISION));
}

export function isHundredPercent(value: number | null | undefined): boolean {
  const normalized = normalizePercentage(value);
  if (normalized === null) {
    return false;
  }
  return Math.abs(normalized - 100) < 0.00001;
}

export function formatPercentageDisplay(
  value: number | null | undefined
): string {
  const normalized = normalizePercentage(value) ?? 0;
  return normalized.toFixed(PERCENTAGE_PRECISION);
}

/* ------------------------------------------------------------------ */
/* Granularity-agnostic helpers                                        */
/* ------------------------------------------------------------------ */

const ISO_DATE_FMT = "yyyy-MM-dd";

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export function leapYearsInRange(start: Date, end: Date): number[] {
  return eachYearOfInterval({ start, end })
    .map((d) => d.getFullYear())
    .filter(isLeapYear);
}

/**
 * Parse a plain `yyyy-MM-dd` into a local-time Date at midnight, avoiding
 * the timezone off-by-one that `new Date("2026-04-01")` produces in negative
 * UTC offsets.
 */
export function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

export function formatIsoDate(date: Date): string {
  return format(date, ISO_DATE_FMT);
}

/**
 * A PPA contract is expired once its end_date falls before the reference calendar day; no end_date means
 * perpetual. Expired PPAs are inert: the backend skips them in allocation and in cross-PPA validation, so
 * their devices are free for a successor PPA.
 */
export function isPpaContractExpired(
  instructions: ActionInstructions | null | undefined,
  referenceDate: Date = new Date()
): boolean {
  if (!instructions?.end_date) {
    return false;
  }
  return endOfDay(parseIsoDate(instructions.end_date)) < referenceDate;
}

const DURATION_FOR_GRANULARITY: Record<LimitGranularity, string> = {
  monthly: "P1M",
  quarterly: "P3M",
  yearly: "P1Y",
};

// P12M and P1Y represent the same duration; existing data uses both.
const YEARLY_DURATION_ALIASES = new Set(["P1Y", "P12M"]);

function normalizeDuration(duration: string): string {
  return YEARLY_DURATION_ALIASES.has(duration) ? "P1Y" : duration;
}

/**
 * Infer granularity from the existing limits by taking the modal duration.
 * Defaults to "yearly" when there are no limits.
 */
export function determineLimitGranularity(
  limits: PeriodLimit[]
): LimitGranularity {
  if (!limits || limits.length === 0) return "yearly";

  const counts = new Map<string, number>();
  for (const limit of limits) {
    const d = normalizeDuration(limit.period.duration);
    counts.set(d, (counts.get(d) ?? 0) + 1);
  }

  let modal = normalizeDuration(limits[0].period.duration);
  let best = 0;
  for (const [d, c] of counts) {
    if (c > best) {
      best = c;
      modal = d;
    }
  }

  if (modal === "P1Y") return "yearly";
  // P{4-11}M only arises from yearly partial-year edges (monthly=P1M,
  // quarterly edges=P1M/P2M/P3M), so a modal in that range is a short or
  // partial yearly contract.
  if (/^P([4-9]|1[01])M$/.test(modal)) return "yearly";
  // P2M only arises from a partial-quarter edge (monthly mode is uniformly
  // P1M), so a P2M-modal list is a very short quarterly contract.
  if (modal === "P3M" || modal === "P2M") return "quarterly";
  return "monthly";
}

/**
 * One boundary in a contract's limit timeline. The `start`/`duration` pair
 * always aligns to calendar periods (month or year) so the backend allocation
 * algorithm can bucket certificates by production month. `isPartial` is true
 * either when the stored duration is shorter than a natural period (e.g., P2M
 * at a quarterly edge) OR when the contract itself starts/ends mid-period
 * (e.g., monthly cell where the user entered a start date on the 15th).
 *
 * For partial edge cells, `partialStart`/`partialEnd` describe the actual
 * contract-bounded range the cell represents (inclusive). Labels use this to
 * show "May (15–31)" or "Q2 (15 May – 30 Jun)".
 */
interface LimitBoundary {
  start: Date;
  // P1Y for full years; P{1-11}M for partial quarterly/yearly edges and for
  // monthly boundaries.
  duration: string;
  isPartial: boolean;
  partialStart?: Date;
  partialEnd?: Date;
}

type QuarterBoundary = LimitBoundary & {
  duration: "P1M" | "P2M" | "P3M";
};

function naturalQuarterStart(date: Date): Date {
  const month = date.getMonth();
  return new Date(date.getFullYear(), month - (month % 3), 1);
}

function monthDurationCode(months: number): "P1M" | "P2M" | "P3M" {
  if (months === 1) return "P1M";
  if (months === 2) return "P2M";
  return "P3M";
}

/**
 * Exclusive boundary (first-of-month) immediately AFTER `contractEnd`'s month.
 * All boundary enumerators iterate up to (but excluding) this date.
 */
function exclusiveEndBoundary(contractEnd: Date): Date {
  return addMonths(startOfMonth(contractEnd), 1);
}

/** Inclusive last day of the boundary covering `cursor`..`nextCursor`. */
function boundaryInclusiveEnd(nextCursor: Date): Date {
  return addDays(nextCursor, -1);
}

/** Strip time-of-day so date-only comparisons don't false-positive when
 *  callers pass dates built with `endOfMonth`/`endOfYear` (which carry
 *  23:59:59.999). */
function atMidnight(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Intersect a calendar boundary [boundaryStart, boundaryEndInclusive] with the
 * contract range [contractStart, contractEnd]. Returns the actual partial
 * range (or null when the cell fully covers the boundary).
 */
function computePartialRange(
  boundaryStart: Date,
  boundaryEndInclusive: Date,
  contractStart: Date,
  contractEnd: Date
): { partialStart: Date; partialEnd: Date } | null {
  const bStart = atMidnight(boundaryStart);
  const bEnd = atMidnight(boundaryEndInclusive);
  const cStart = atMidnight(contractStart);
  const cEnd = atMidnight(contractEnd);
  const hasEarlyStart = cStart > bStart;
  const hasEarlyEnd = cEnd < bEnd;
  if (!hasEarlyStart && !hasEarlyEnd) return null;
  return {
    partialStart: hasEarlyStart ? cStart : bStart,
    partialEnd: hasEarlyEnd ? cEnd : bEnd,
  };
}

export function computeQuarterlyLimitBoundaries(
  contractStart: Date,
  contractEnd: Date
): QuarterBoundary[] {
  const start = startOfMonth(contractStart);
  const boundary = exclusiveEndBoundary(contractEnd);

  const results: QuarterBoundary[] = [];
  let cursor = start;
  while (cursor < boundary) {
    const naturalNext = addMonths(naturalQuarterStart(cursor), 3);
    const nextStop = naturalNext < boundary ? naturalNext : boundary;
    const months = differenceInCalendarMonths(nextStop, cursor);
    const calendarEndInclusive = boundaryInclusiveEnd(nextStop);
    const partial = computePartialRange(
      cursor,
      calendarEndInclusive,
      contractStart,
      contractEnd
    );
    results.push({
      start: cursor,
      duration: monthDurationCode(months),
      isPartial: months !== 3 || partial !== null,
      ...(partial ?? {}),
    });
    cursor = nextStop;
  }
  return results;
}

/**
 * Build a PeriodLimit with a clamped, rounded-to-integer MWh value and a
 * given start+duration pair. All callers that emit PeriodLimits go through
 * this so rounding behavior stays consistent.
 */
function makePeriodLimit(
  start: Date,
  duration: string,
  mwh: number
): PeriodLimit {
  return {
    period: { start_date: formatIsoDate(start), duration },
    limit_mwh: Math.max(0, Math.round(mwh)),
  };
}

/**
 * Create a new PeriodLimit using the natural duration for the granularity
 * (P1M/P3M/P1Y), or a caller-supplied duration (e.g. P2M for a partial
 * quarter).
 */
export function createPeriodLimit(
  granularity: LimitGranularity,
  startDate: Date,
  mwh: number,
  duration?: string
): PeriodLimit {
  return makePeriodLimit(
    startDate,
    duration ?? DURATION_FOR_GRANULARITY[granularity],
    mwh
  );
}

/**
 * Expand a `reference_year_values` pattern from a `portfolio_instructions_patch`
 * into a flat `PeriodLimit[]` spanning the contract range. Monthly contracts
 * with a leap-year February override apply `leap_feb_override` only in leap
 * years. Returns `[]` when the pattern shape doesn't match the declared
 * granularity so callers can treat it as "nothing to apply".
 */
export function applyReferencePattern(
  patch: PortfolioInstructionsPatch,
  contractStart: Date,
  contractEnd: Date
): PeriodLimit[] {
  const granularity: LimitGranularity = patch.granularity ?? "monthly";
  const values = patch.reference_year_values;
  if (!values || values.length === 0) return [];

  if (granularity === "monthly") {
    if (values.length !== 12) return [];
    const result: PeriodLimit[] = [];
    const endBoundary = addMonths(startOfMonth(contractEnd), 1);
    let cursor = startOfMonth(contractStart);
    while (cursor < endBoundary) {
      const month = cursor.getMonth();
      const baseValue = values[month];
      const mwh =
        month === 1 &&
        patch.leap_feb_override != null &&
        isLeapYear(cursor.getFullYear())
          ? patch.leap_feb_override
          : baseValue;
      result.push(createPeriodLimit("monthly", cursor, mwh));
      cursor = addMonths(cursor, 1);
    }
    return result;
  }

  if (granularity === "quarterly") {
    if (values.length !== 4) return [];
    return computeQuarterlyLimitBoundaries(contractStart, contractEnd).map(
      (b) =>
        makePeriodLimit(
          b.start,
          b.duration,
          values[Math.floor(b.start.getMonth() / 3)]
        )
    );
  }

  // yearly — expand across the contract range using the same boundary
  // builder the matrix uses, so partial first/last years get month-aligned
  // starts and P{N}M durations (instead of claiming Jan–Dec coverage for
  // months outside the contract).
  if (values.length !== 1) return [];
  return computeYearlyLimitBoundaries(contractStart, contractEnd).map((b) =>
    makePeriodLimit(b.start, b.duration, values[0])
  );
}

/**
 * Summarize the partial-period situation for a contract's date range, for
 * display next to the contract-dates input. Returns `null` when no edge cell
 * is trimmed by the contract bounds.
 */
export function describePartialBoundary(
  contractStart: Date,
  contractEnd: Date,
  granularity: LimitGranularity
): { firstRange: string; lastRange: string; count: number } | null {
  if (contractEnd <= contractStart) return null;
  const boundaries = buildEmptyBoundaries(
    granularity,
    contractStart,
    contractEnd
  );
  // After the ContractDatesSection month-snap, day-level trims only arise
  // for yearly boundaries (year starts Jan 1 but contract starts May 1);
  // quarterly edges become P2M/P1M boundaries with `isPartial: true` but
  // no sub-month partialStart. Treat both as "partial periods worth
  // surfacing" and derive the display range from the boundary itself when
  // day-level info is absent.
  const partials = boundaries.filter((b) => b.isPartial);
  if (partials.length === 0) return null;
  const rangeFor = (b: LimitBoundary): [Date, Date] => {
    if (b.partialStart && b.partialEnd) return [b.partialStart, b.partialEnd];
    const end = addDays(addMonths(b.start, monthsInDuration(b.duration)), -1);
    return [b.start, end];
  };
  const [fs, fe] = rangeFor(partials[0]);
  const [ls, le] = rangeFor(partials[partials.length - 1]);
  return {
    firstRange: formatPartialRangeLabel(fs, fe),
    lastRange: formatPartialRangeLabel(ls, le),
    count: partials.length,
  };
}

/* ------------------------------------------------------------------ */
/* Year-grouped projection                                             */
/* ------------------------------------------------------------------ */

/** One cell in a year card (a single PeriodLimit). */
export interface YearGroupCell {
  /** 0-based position within the year (0..11 monthly, 0..3 quarterly). */
  periodIndex: number;
  mwh: number;
  startDate: string;
  /** "P1Y" for full years; "P{1-11}M" for partial quarterly/yearly edges and
   *  monthly cells. */
  duration: string;
  /** True when this cell covers fewer months than a natural period OR when
   *  the contract itself starts/ends mid-period. */
  isPartial: boolean;
  /** Human label ("Jan", "Q2", "Q2 (May–Jun)"). */
  label: string;
  /** Day-precise partial coverage label ("15–31 May", "15 May – 30 Jun",
   *  "15 May – 31 Dec"). Populated only when the contract's start/end trims
   *  this cell inside its calendar boundary. */
  partialRangeLabel?: string;
  /** True for February cells in leap years (monthly only). */
  isLeapFeb: boolean;
}

export interface YearGroup {
  year: number;
  cells: YearGroupCell[];
  isFirstYear: boolean;
  isLastYear: boolean;
  isLeapYear: boolean;
}

function naturalQuarterIndex(monthZeroBased: number): number {
  return Math.floor(monthZeroBased / 3);
}

function quarterCellLabel(
  startMonth: number,
  months: number,
  hasMonthLevelPartial: boolean
): string {
  const qIndex = naturalQuarterIndex(startMonth);
  const base = `Q${qIndex + 1}`;
  if (!hasMonthLevelPartial) return base;
  const endMonth = startMonth + months - 1;
  return `${base} (${MONTH_NAMES[startMonth]}–${MONTH_NAMES[endMonth]})`;
}

/**
 * Human-readable partial coverage range for display ("May–Jun",
 * "May–Dec", "May 2026 – Mar 2027"). Contract dates are snapped to
 * month boundaries on input (GOs only exist in monthly buckets), so
 * labels collapse to month granularity.
 */
function formatPartialRangeLabel(start: Date, end: Date): string {
  const sameYear = start.getFullYear() === end.getFullYear();
  if (sameYear) {
    return `${MONTH_NAMES[start.getMonth()]}–${MONTH_NAMES[end.getMonth()]}`;
  }
  return `${MONTH_NAMES[start.getMonth()]} ${start.getFullYear()} – ${MONTH_NAMES[end.getMonth()]} ${end.getFullYear()}`;
}

function cellFromBoundary(
  granularity: LimitGranularity,
  boundary: LimitBoundary,
  mwh: number,
  leapYear: boolean
): YearGroupCell {
  const startMonth = boundary.start.getMonth();
  const baseCell = {
    mwh,
    startDate: formatIsoDate(boundary.start),
    duration: boundary.duration,
    isPartial: boundary.isPartial,
    ...(boundary.partialStart && boundary.partialEnd
      ? {
          partialRangeLabel: formatPartialRangeLabel(
            boundary.partialStart,
            boundary.partialEnd
          ),
        }
      : {}),
  };

  if (granularity === "yearly") {
    return {
      ...baseCell,
      periodIndex: 0,
      label: String(boundary.start.getFullYear()),
      isLeapFeb: false,
    };
  }
  if (granularity === "monthly") {
    return {
      ...baseCell,
      periodIndex: startMonth,
      label: MONTH_NAMES[startMonth],
      isLeapFeb: leapYear && startMonth === 1,
    };
  }
  // quarterly: only include month-range suffix when the quarter is shorter
  // than a full P3M (partial by month-count). Day-level partials surface via
  // `partialRangeLabel` instead of polluting the primary label.
  const hasMonthLevelPartial = boundary.duration !== "P3M";
  return {
    ...baseCell,
    periodIndex: naturalQuarterIndex(startMonth),
    label: quarterCellLabel(
      startMonth,
      monthsInDuration(boundary.duration),
      hasMonthLevelPartial
    ),
    isLeapFeb: false,
  };
}

function buildEmptyBoundaries(
  granularity: LimitGranularity,
  contractStart: Date,
  contractEnd: Date
): LimitBoundary[] {
  if (granularity === "quarterly") {
    return computeQuarterlyLimitBoundaries(contractStart, contractEnd);
  }
  if (granularity === "monthly") {
    const months = eachMonthOfInterval({
      start: startOfMonth(contractStart),
      end: startOfMonth(contractEnd),
    });
    // An edge month is "partial" when the contract doesn't cover its full
    // calendar month — first month with a contract start past day 1, or
    // last month with a contract end before month-end. Mid-range months
    // always cover a full month.
    return months.map((start) => {
      const calendarEnd = endOfMonth(start);
      const partial = computePartialRange(
        start,
        calendarEnd,
        contractStart,
        contractEnd
      );
      return {
        start,
        duration: "P1M",
        isPartial: partial !== null,
        ...(partial ?? {}),
      };
    });
  }
  // yearly: walk the contract range in calendar-year chunks. Full years emit
  // P1Y at Jan 1; partial edge years emit P{N}M anchored at the contract's
  // actual start-of-month (or Jan 1 of the final year) so the stored limit
  // never overclaims coverage outside the contract.
  return computeYearlyLimitBoundaries(contractStart, contractEnd);
}

function computeYearlyLimitBoundaries(
  contractStart: Date,
  contractEnd: Date
): LimitBoundary[] {
  const start = startOfMonth(contractStart);
  const boundary = exclusiveEndBoundary(contractEnd);

  const results: LimitBoundary[] = [];
  let cursor = start;
  while (cursor < boundary) {
    const naturalNext = new Date(cursor.getFullYear() + 1, 0, 1);
    const nextStop = naturalNext < boundary ? naturalNext : boundary;
    const months = differenceInCalendarMonths(nextStop, cursor);
    const duration = months === 12 ? "P1Y" : `P${months}M`;
    const calendarStart = new Date(cursor.getFullYear(), 0, 1);
    const calendarEnd = endOfYear(calendarStart);
    const partial = computePartialRange(
      calendarStart,
      calendarEnd,
      contractStart,
      contractEnd
    );
    results.push({
      start: cursor,
      duration,
      isPartial: months !== 12 || partial !== null,
      ...(partial ?? {}),
    });
    cursor = nextStop;
  }
  return results;
}

/**
 * Group a flat PeriodLimit[] list into per-year cards over the contract
 * range. For cells with no existing limit (e.g., when the matrix is empty
 * or granularity just switched), a zero-valued cell is produced.
 */
export function groupLimitsByYear(
  limits: PeriodLimit[],
  granularity: LimitGranularity,
  contractStart: Date,
  contractEnd: Date
): YearGroup[] {
  const limitsByStart = new Map<string, number>();
  for (const limit of limits) {
    limitsByStart.set(limit.period.start_date, limit.limit_mwh);
  }

  const boundaries = buildEmptyBoundaries(
    granularity,
    contractStart,
    contractEnd
  );
  const byYear = new Map<number, YearGroup>();

  for (const b of boundaries) {
    const year = b.start.getFullYear();
    if (!byYear.has(year)) {
      byYear.set(year, {
        year,
        cells: [],
        isFirstYear: false,
        isLastYear: false,
        isLeapYear: isLeapYear(year),
      });
    }
    const mwh = limitsByStart.get(formatIsoDate(b.start)) ?? 0;
    byYear
      .get(year)!
      .cells.push(cellFromBoundary(granularity, b, mwh, isLeapYear(year)));
  }

  const years = Array.from(byYear.values()).sort((a, b) => a.year - b.year);
  if (years.length > 0) {
    years[0].isFirstYear = true;
    years[years.length - 1].isLastYear = true;
  }
  return years;
}

/**
 * Flatten year groups back to a flat PeriodLimit[] list.
 */
export function flattenYearGroups(groups: YearGroup[]): PeriodLimit[] {
  return groups.flatMap((g) =>
    g.cells.map((c) =>
      makePeriodLimit(parseIsoDate(c.startDate), c.duration, c.mwh)
    )
  );
}

/**
 * Project an existing flat limits list onto the current contract boundary
 * shape. Cells outside the new range are dropped; new partial cells added
 * by the boundary shape appear with 0 MWh. Durations always match the
 * boundary's duration — callers relying on matching start_date + duration
 * pairs can pass the result straight through save/validation.
 */
export function projectLimitsToContractRange(
  limits: PeriodLimit[],
  granularity: LimitGranularity,
  contractStart: Date,
  contractEnd: Date
): PeriodLimit[] {
  return flattenYearGroups(
    groupLimitsByYear(limits, granularity, contractStart, contractEnd)
  );
}

/** Structural equality check on PeriodLimit lists (order-sensitive). */
export function sameLimits(a: PeriodLimit[], b: PeriodLimit[]): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (limit, i) =>
      limit.period.start_date === b[i].period.start_date &&
      limit.period.duration === b[i].period.duration &&
      limit.limit_mwh === b[i].limit_mwh
  );
}

/**
 * Copy the full-period (non-partial) cells of `sourceYear` to every other
 * year, matching by period index. Partial cells in target years are left
 * untouched; the source's partial cells are not copied.
 */
export function applyYearValuesToAllYears(
  groups: YearGroup[],
  sourceYear: number
): YearGroup[] {
  const source = groups.find((g) => g.year === sourceYear);
  if (!source) return groups;

  const sourceFullCellsByIndex = new Map<number, number>();
  for (const cell of source.cells) {
    if (!cell.isPartial) {
      sourceFullCellsByIndex.set(cell.periodIndex, cell.mwh);
    }
  }

  return groups.map((g) => {
    if (g.year === sourceYear) return g;
    return {
      ...g,
      cells: g.cells.map((cell) => {
        if (cell.isPartial) return cell;
        const candidate = sourceFullCellsByIndex.get(cell.periodIndex);
        if (candidate === undefined) return cell;
        return { ...cell, mwh: candidate };
      }),
    };
  });
}

/* ------------------------------------------------------------------ */
/* Granularity transitions                                             */
/* ------------------------------------------------------------------ */

export function monthsInDuration(duration: string): number {
  if (YEARLY_DURATION_ALIASES.has(duration)) return 12;
  const match = /^P(\d+)M$/.exec(duration);
  if (match) {
    const n = Number(match[1]);
    if (n >= 1 && n <= 12) return n;
  }
  return 1;
}

/**
 * Convert a limits list from one granularity to another by prorating each
 * source limit's MWh across the target boundaries by month-overlap. Preserves
 * per-period values on monthly↔quarterly switches (a quarterly source splits
 * evenly across its 3 months; 3 monthly sources sum into one quarterly cell)
 * and gracefully handles partial first/last periods in the contract range.
 *
 * Conservation: for a source fully within the contract range, the sum of its
 * contributions equals the source MWh exactly (floor + remainder spread to
 * the earliest overlapping buckets). Sources outside the range are dropped.
 */
export function changeLimitGranularity(
  existing: PeriodLimit[],
  from: LimitGranularity,
  to: LimitGranularity,
  contractStart: Date,
  contractEnd: Date
): PeriodLimit[] {
  if (from === to) return existing;

  const targetBoundaries = buildEmptyBoundaries(to, contractStart, contractEnd);
  if (targetBoundaries.length === 0) return [];
  const targetEnds = targetBoundaries.map((b) =>
    addMonths(b.start, monthsInDuration(b.duration))
  );
  const contributions = new Array<number>(targetBoundaries.length).fill(0);

  for (const limit of existing) {
    const mwh = limit.limit_mwh;
    if (!mwh) continue;
    const sourceStart = parseIsoDate(limit.period.start_date);
    const sourceEnd = addMonths(
      sourceStart,
      monthsInDuration(limit.period.duration)
    );

    const overlapMonths = targetBoundaries.map((b, i) => {
      const overlapStart = maxDate([sourceStart, b.start]);
      const overlapEnd = minDate([sourceEnd, targetEnds[i]]);
      return Math.max(0, differenceInCalendarMonths(overlapEnd, overlapStart));
    });

    const totalOverlap = sum(overlapMonths);
    if (totalOverlap === 0) continue;

    // Floor per-bucket, then distribute the rounding remainder to the earliest
    // overlapping buckets so the source total is preserved exactly.
    const floored = overlapMonths.map((m) =>
      Math.floor((mwh * m) / totalOverlap)
    );
    let remainder = mwh - sum(floored);
    for (let i = 0; i < floored.length && remainder > 0; i++) {
      if (overlapMonths[i] > 0) {
        floored[i] += 1;
        remainder -= 1;
      }
    }
    floored.forEach((v, i) => {
      contributions[i] += v;
    });
  }

  return targetBoundaries.map((b, i) =>
    makePeriodLimit(b.start, b.duration, contributions[i])
  );
}

/* ------------------------------------------------------------------ */
/* Existing API (yearly-only helpers)                                  */
/* ------------------------------------------------------------------ */

function createNewPeriodLimit(existingLimits: PeriodLimit[]): PeriodLimit {
  const currentYear = new Date().getFullYear();
  const existingStartDates = new Set(
    existingLimits.map((limit) => limit.period.start_date)
  );

  let year = currentYear + 1;
  let startDate = `${year}-01-01`;

  while (existingStartDates.has(startDate)) {
    year++;
    startDate = `${year}-01-01`;
  }

  return {
    period: {
      start_date: startDate,
      duration: "P1Y",
    },
    limit_mwh: 0,
  };
}

/**
 * Top-level allocation mode (independent from granularity).
 */
export type AllocationMode = "all" | "percentage" | "fixed-amounts";

export function determineAllocationMode(
  allocatePercentage: number | null | undefined,
  limits: PeriodLimit[]
): AllocationMode {
  if (limits.length > 0) {
    return "fixed-amounts";
  }
  if (isHundredPercent(allocatePercentage)) {
    return "all";
  }
  return "percentage";
}

export function configureAllocationMode(
  mode: AllocationMode,
  currentPercentage: number | null | undefined,
  currentLimits: PeriodLimit[],
  _minimumPrice?: number
): {
  allocatePercentage: number;
  limits: PeriodLimit[];
} {
  const normalizedCurrent = normalizePercentage(currentPercentage);

  switch (mode) {
    case "all":
      return { allocatePercentage: 100, limits: [] };

    case "fixed-amounts":
      return {
        allocatePercentage: 100,
        limits:
          currentLimits.length > 0 ? currentLimits : [createNewPeriodLimit([])],
      };

    case "percentage": {
      const fallbackPercentage =
        normalizedCurrent !== null &&
        !isHundredPercent(normalizedCurrent) &&
        normalizedCurrent > 0
          ? normalizedCurrent
          : 50;

      return {
        allocatePercentage: Number.parseFloat(
          fallbackPercentage.toFixed(PERCENTAGE_PRECISION)
        ),
        limits: [],
      };
    }

    default:
      return {
        allocatePercentage:
          normalizedCurrent !== null
            ? Number.parseFloat(normalizedCurrent.toFixed(PERCENTAGE_PRECISION))
            : 0,
        limits: currentLimits,
      };
  }
}

/**
 * Check that every limit's duration matches the granularity contract:
 * - yearly: all P1Y
 * - monthly: all P1M
 * - quarterly: interior limits P3M; the first and/or last may be P1M/P2M (partial).
 */
export function hasHomogeneousDurations(
  limits: PeriodLimit[],
  granularity: LimitGranularity
): boolean {
  if (limits.length === 0) return true;
  if (granularity === "yearly") {
    const sorted = [...limits].sort((a, b) =>
      a.period.start_date.localeCompare(b.period.start_date)
    );
    return sorted.every((l, idx) => {
      const d = l.period.duration;
      if (YEARLY_DURATION_ALIASES.has(d)) return true;
      const isEdge = idx === 0 || idx === sorted.length - 1;
      return isEdge && /^P([1-9]|1[01])M$/.test(d);
    });
  }
  if (granularity === "monthly") {
    return limits.every((l) => l.period.duration === "P1M");
  }
  // quarterly
  const sorted = [...limits].sort((a, b) =>
    a.period.start_date.localeCompare(b.period.start_date)
  );
  return sorted.every((l, idx) => {
    const isEdge = idx === 0 || idx === sorted.length - 1;
    if (isEdge) return ["P1M", "P2M", "P3M"].includes(l.period.duration);
    return l.period.duration === "P3M";
  });
}

/**
 * Human label for a single limit (e.g. "2026", "May 2026", "Q2 2026",
 * "May–Jun 2026" for partial quarters).
 */
export function periodLabelForLimit(limit: PeriodLimit): string {
  const start = parseIsoDate(limit.period.start_date);
  const year = start.getFullYear();
  const duration = limit.period.duration;

  if (YEARLY_DURATION_ALIASES.has(duration)) return String(year);

  const startMonth = start.getMonth();
  if (duration === "P1M") return `${MONTH_NAMES[startMonth]} ${year}`;

  if (duration === "P3M" && startMonth % 3 === 0) {
    return `Q${naturalQuarterIndex(startMonth) + 1} ${year}`;
  }

  if (duration === "P2M" || duration === "P3M") {
    const endMonth = startMonth + monthsInDuration(duration) - 1;
    return `${MONTH_NAMES[startMonth]}–${MONTH_NAMES[endMonth]} ${year}`;
  }

  return String(year);
}

/**
 * Find the limit whose [start, start+duration) contains `at`, or null.
 */
export function findLimitAt(
  limits: PeriodLimit[],
  at: Date
): PeriodLimit | null {
  for (const limit of limits) {
    const start = parseIsoDate(limit.period.start_date);
    const end = add(start, parseIsoDuration(limit.period.duration));
    if (at >= start && at < end) return limit;
  }
  return null;
}
